import { useSyncExternalStore } from "react";

import { CartService } from "@/components/Cart/CartService";
import { useIsLocalisedPricing } from "@/hooks/useIsLocalisedPricing";
import { TBillingInterval } from "@/types/app";
import { useQuery } from "@tanstack/react-query";
import { CHECKOUT_EVENTS } from "../../events.checkout";
import {
  CheckoutSummaryPayload,
  getCheckoutSummary,
} from "../../handlers/getCheckoutSummary";
import { CheckoutService } from "../../service.checkout";
import { TCheckoutType } from "../../types.checkout";

export const CHECKOUT_SUMMARY_QUERY_KEY = "checkout-summary";

interface UseCheckoutSummaryQueryOptions {
  checkoutType: TCheckoutType;
  billingPeriod: TBillingInterval;
  enabled?: boolean;
}

export function useCheckoutSummaryQuery({
  checkoutType,
  billingPeriod,
  enabled = true,
}: UseCheckoutSummaryQueryOptions) {
  const userToken = CheckoutService.userToken;
  const { isLocalisedPricing } = useIsLocalisedPricing();

  const cart = useSyncExternalStore(
    CartService.subscribe,
    () => CartService.getState().cart,
  );

  const appliedCodes = useSyncExternalStore(
    CartService.subscribe,
    () => CartService.getState().promo.appliedCodes,
  );

  const itemsKey = cart
    .map((i) => `${i.id}:${i.quantity ?? 1}:${i.type}`)
    .sort()
    .join(",");
  const codesKey = appliedCodes.join(",");

  const query = useQuery({
    queryKey: [
      CHECKOUT_SUMMARY_QUERY_KEY,
      checkoutType,
      billingPeriod,
      itemsKey,
      codesKey,
      // Keyed so a USD summary cached before the region resolved isn't reused.
      isLocalisedPricing,
    ],
    queryFn: async () => {
      const payload: CheckoutSummaryPayload = {
        products: cart.map((item) => ({
          product_id: item.id,
          action: "add" as const,
          quantity: item.quantity || 1,
        })),
        interval: billingPeriod,
        discounts: appliedCodes.length > 0 ? appliedCodes : undefined,
      };

      try {
        const data = await getCheckoutSummary({
          token: userToken!,
          payload,
          checkoutType,
          isLocalisedPricing,
        });
        CheckoutService.track(CHECKOUT_EVENTS.SUMMARY_LOADED, {
          types: ["posthog", "customerio"],
          properties: {
            checkout_type: checkoutType,
            payload,
          },
        });
        return data;
      } catch (error) {
        CheckoutService.logger.error(
          "Failed to fetch checkout summary:",
          error,
          payload,
        );
        CheckoutService.track(CHECKOUT_EVENTS.SUMMARY_FAILED, {
          types: ["posthog", "customerio"],
          properties: {
            checkout_type: checkoutType,
            payload,
            error: error instanceof Error ? error.message : "Unknown error",
          },
        });
        throw error;
      }
    },
    enabled: enabled && !!userToken && cart.length > 0,
    staleTime: 2 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  return {
    summary: query.data ?? null,
    isLoading: query.isFetching,
    isError: query.isError,
    error: query.error,
  };
}
